import { getLocalStorage, saveLocalStorage } from "@elit-hair/elit-tools";
import { DateTime } from "luxon";
import { useCallback, useEffect, useSyncExternalStore } from "react";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { useCall } from "@/providers/hooks/use-call";
import type { LastCallRecord } from "@/types/last-call.types";
import { getBrandFromOrgId } from "@/utils/get-brand-from-org-id";

const LAST_CALL_KEY = LOCAL_STORAGE_KEYS.LAST_CALL.key;

const listeners = new Set<() => void>();

// The in-memory call is still available after a clear. Skip saving it again until a new call starts.
let ignoredCallKey: string | null = null;

// Cached so useSyncExternalStore can compare snapshots by reference.
// Read lazily: a module-level read can run before this preview can access localStorage.
let snapshot: LastCallRecord | null | undefined;

function readStoredLastCall(): LastCallRecord | null {
    const stored = getLocalStorage<LastCallRecord | null>(LAST_CALL_KEY, null);
    if (!stored?.data || !stored.moduleType || !stored.orgId) {
        return null;
    }

    return stored;
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot(): LastCallRecord | null {
    if (snapshot === undefined) {
        snapshot = readStoredLastCall();
    }

    return snapshot;
}

function emit() {
    listeners.forEach((listener) => listener());
}

function setLastCallRecord(value: LastCallRecord | null) {
    if (snapshot === undefined) {
        snapshot = readStoredLastCall();
    }

    if (JSON.stringify(value) === JSON.stringify(snapshot)) {
        return;
    }

    saveLocalStorage(LAST_CALL_KEY, value);
    snapshot = value;
    emit();
}

export function useLastCallRecord() {
    const { startCall, currentRecordInformation, currentCallData } = useCall();
    const lastCallRecord = useSyncExternalStore(subscribe, getSnapshot);

    useEffect(() => {
        if (currentRecordInformation && currentCallData?.orgId) {
            const brand = getBrandFromOrgId(currentCallData.orgId);

            if (currentCallData.moduleType === "Leads" || currentCallData.moduleType === "Contacts") {
                const callKey = currentCallData.id || currentRecordInformation.id;
                if (callKey && callKey === ignoredCallKey) {
                    return;
                }

                ignoredCallKey = null;

                const existing = getSnapshot();
                const sameRecord = existing?.data.id === currentRecordInformation.id;
                const calledAt =
                    currentCallData.callStartTime ||
                    (sameRecord ? existing?.calledAt : undefined) ||
                    DateTime.now().toISO() ||
                    new Date().toISOString();

                setLastCallRecord({
                    data: currentRecordInformation,
                    moduleType: currentCallData.moduleType,
                    orgId: currentCallData.orgId,
                    brand,
                    callPurpose: currentCallData.callPurpose,
                    calledAt,
                });
            }
        }
    }, [currentRecordInformation, currentCallData]);

    const clearLastCallRecord = useCallback(() => {
        ignoredCallKey = currentCallData?.id || lastCallRecord?.data.id || ignoredCallKey;
        setLastCallRecord(null);
    }, [currentCallData?.id, lastCallRecord?.data.id]);

    const callLastRecord = useCallback(
        (phoneNumber: string) => {
            if (!lastCallRecord) {
                startCall(phoneNumber);
                return;
            }

            const calledName = lastCallRecord.data.Full_Name?.trim();
            const calledEmail = lastCallRecord.data.Email?.trim();

            startCall(phoneNumber, {
                moduleType: lastCallRecord.moduleType,
                recordId: lastCallRecord.data.id,
                orgId: lastCallRecord.orgId,
                ...(lastCallRecord.callPurpose ? { callPurpose: lastCallRecord.callPurpose } : {}),
                ...(calledName ? { calledName } : {}),
                ...(calledEmail ? { calledEmail } : {}),
            });
        },
        [startCall, lastCallRecord]
    );

    return { lastCallRecord, clearLastCallRecord, callLastRecord };
}
